import React, { useEffect, useState } from 'react';
import useScrollArrows from '@hooks/use-scroll-arrows';
import Translate from '@utils/translation-functions';
import TabTitles from '@blocks/tabbed-block/tab-titles';
import StyledVisualisationBlock, { ContentType } from './visualisation-block';
import { HighlightAtom } from '@atoms/text-atoms/text-atoms';
import {
  LeftScrollArrow,
  RightScrollArrow,
} from '@components/scroll-arrows/scroll-arrows';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import {
  HoneycombContainerBlockProps,
  PageDataProps,
  PageProps,
} from './honeycomb-container.types';
import { honeycombDataPortuguese } from './honeycomb-data-Portuguese.data';
import { honeycombDataSpanish } from './honeycomb-data-Spanish.data';
import { honeycombData } from './honeycomb-data.data';
import { HoneyCombContainer } from './honeycomb-styles';
import { useDataContext } from '@context/page-context-provider';

const HoneycombContainerBlock: React.FC<HoneycombContainerBlockProps> = ({
  className,
  anchorId,

  translate = true,
}) => {
  const { pageContext } = useDataContext();
  const [selectedButtonIndex, setSelectedButtonIndex] = useState(0);
  const [buttons, setButtons] = useState([]);
  const [contentArray, setContentArray] = useState<PageProps[]>([]);
  const [currentTab, setCurrentTab] = useState<number>(0);
  const tabsRef = React.useRef<HTMLUListElement>(null);
  const { showLeftArrow, showRightArrow, scrollTabs } = useScrollArrows({
    tabsRef,
  });
  const [mappedTabs, setMappedTabs] = useState<
    { id: string; tabTitle: string }[]
  >([]);

  useEffect(() => {
    if (!honeycombData) {
      console.error('No data available.');
      return;
    }

    let pageData;

    switch (pageContext.locale) {
      case 'pt':
        pageData = honeycombDataPortuguese as PageDataProps;
        break;
      case 'es':
        pageData = honeycombDataSpanish as PageDataProps;
        break;
      default:
        pageData = honeycombData as PageDataProps;
        break;
    }

    const contentArrayWithID = pageData.contentArray.map((page, index) => {
      return { ...page, ID: index };
    });
    setContentArray(contentArrayWithID);
    setButtons(pageData.visualisationButtons || []);

    if (!pageData.contentArray) {
      console.error('No array of tabs and content found.');
    }
    if (!pageData.visualisationButtons) {
      console.error('No buttons for main content found.');
    }
  }, [pageContext.locale]);

  const handleTabClick = index => {
    setCurrentTab(index);
  };

  useEffect(() => {
    setMappedTabs([
      ...contentArray.map(page => {
        return {
          id: page.ID.toString(),
          tabTitle: page.tabTitle,
        };
      }),
    ]);
  }, [contentArray, tabsRef]);

  return (
    <HoneyCombContainer
      id={anchorId}
      className={className}
      data-testid="honeycomb-block"
      anchorId={anchorId}
    >
      <div className="hc-block-tabs-list">
        {showLeftArrow && (
          <LeftScrollArrow
            showLeftArrow={showLeftArrow}
            scrollTabs={scrollTabs}
          />
        )}
        {showRightArrow && (
          <RightScrollArrow
            showRightArrow={showRightArrow}
            scrollTabs={scrollTabs}
          />
        )}

        <TabTitles
          tabs={mappedTabs}
          activeTab={currentTab}
          onTabClick={handleTabClick}
          parentRef={tabsRef}
          gtmId={GTM_ID.HONEYCOMB_TAB}
        />
      </div>
      <div className="tabs-separator" />
      <div className="columns-wrapper">
        <div className="left-column">
          {contentArray.length > 0 &&
            contentArray.map((page, index) => {
              if (page.ID === currentTab) {
                return (
                  <React.Fragment key={page.ID}>
                    {page.leftColumnTitle && (
                      <HighlightAtom>
                        <span className="left-column-title">
                          {page.leftColumnTitle}
                        </span>
                      </HighlightAtom>
                    )}
                    <div className="info-point-wrapper">
                      <div className="info-point">
                        <div className="info-point-text">
                          {Translate(
                            'Scale and shared infrastructure',
                            translate
                          )}
                        </div>
                        <span className="info-point-blob">
                          {page.scenarioParameters?.systemScale}
                        </span>
                        <div className="info-point-blob">
                          {' '}
                          {page.scenarioParameters?.systemScaleSub}
                        </div>
                      </div>
                      <div className="info-point">
                        <span className="info-point-text">
                          {Translate('Packaging system', translate)}:
                        </span>
                        <span className="info-point-blob">
                          {page.scenarioParameters.infrastructure}
                        </span>
                      </div>
                      <div className="info-point">
                        <span className="info-point-text">
                          {Translate('Return rate', translate)}:
                        </span>
                        <span className="info-point-blob">
                          {page.scenarioParameters.returnRate}
                        </span>
                      </div>
                    </div>
                    <div className="map">
                      <img className="map-img" src={page.mapURL} alt="" />
                      <div className="img-caption">{page.imageCaption}</div>
                    </div>
                  </React.Fragment>
                );
              }
              return null;
            })}
        </div>
        <div className="right-column">
          <div className="btn-wrapper">
            {buttons.map((buttonLabel, index) => (
              <button
                data-testid="cta-button-internal-link"
                data-gtm-id="cta-button-internal-link"
                key={index}
                onClick={() => {
                  setSelectedButtonIndex(index);
                }}
                className={index === selectedButtonIndex ? 'btn-selected' : ''}
              >
                {buttonLabel}
              </button>
            ))}
          </div>

          {contentArray.map((page, index) => {
            return (
              <div key={index}>
                <div
                  style={{
                    display:
                      page.ID === currentTab &&
                      buttons[selectedButtonIndex] === buttons[0]
                        ? 'block'
                        : 'none',
                  }}
                >
                  <StyledVisualisationBlock
                    key={page.ID}
                    contentType={ContentType.VIMEO}
                    url={page.vimeoURL}
                  />
                </div>
                <div
                  style={{
                    display:
                      page.ID === currentTab &&
                      buttons[selectedButtonIndex] === buttons[1]
                        ? 'block'
                        : 'none',
                  }}
                >
                  <StyledVisualisationBlock
                    key={page.ID}
                    contentType={ContentType.DATA}
                    dataImageSrc={page.chartURL}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </HoneyCombContainer>
  );
};

export default HoneycombContainerBlock;
